import { CCUtils } from "../../../../common/CCUtils"
import { Align } from "../../../../common/TextUtils"
import { StudentResultsStore } from "../../../../react/students/StudentResultsStore"
import { ButtonContainer, Buttons } from "../../../generic-sprites/Buttons"
import { Rectangle } from "../../../generic-sprites/Shape"
import { Audio } from "../../../utils/Audio"
import { PhaserText } from "../../../utils/PhaserText"
import { Scale } from "../../../utils/Scale"
import { Sprites } from "../../../utils/Sprites"
import { everyCigMoviePreviewImage, EveryCigPart1Audio } from "../EveryCigAssets"
import { EveryCigStore } from "../EveryCigStore"
import { EveryCigBodyPart } from "../EveryCigTypes"
import { EveryCigDropdownScreenContainer } from "./EveryCigDropdownScreenContainer"


export class EveryCigVoteScreenOptionContainer {
    options: Array<EveryCigVoteScreenOption>
    backButton: ButtonContainer
    doneButton: ButtonContainer

    static display = (
        game: Phaser.Game,
        group: Phaser.Group,
        x: number,
        y: number,
        screen: EveryCigDropdownScreenContainer,
        onVote: () => void,
        debug: boolean
    ) => new EveryCigVoteScreenOptionContainer(game, group, x, y, screen, onVote, debug)

    private constructor(
        game: Phaser.Game,
        private group: Phaser.Group,
        x: number,
        y: number,
        private screen: EveryCigDropdownScreenContainer,
        private onVote: () => void,
        debug: boolean
    ) {
        PhaserText.create(game, "Vote for the \"Grossest\" Video", {
            x: Scale.x(0.559),
            y: Scale.y(0.008),
            group,
            fontPx: 20,
            debug: true,
            weight: "bold",
            align: Align.CENTER
        })
        this.backButton = Buttons.create(game, "Back", this.onBackClick, {
            scale: 0.5,
            x: Scale.x(0.556),
            y: Scale.y(0.395),
            debug,
            group
        })
        this.doneButton = Buttons.create(game, "Done", this.submitVote, {
            scale: 0.5,
            x: Scale.x(0.839),
            y: Scale.y(0.395),
            debug,
            group
        })
        this.doneButton.disable()

        let currentX = x
        let currentY = y
        this.options = CCUtils.enumValues(EveryCigBodyPart).map((part: EveryCigBodyPart, index: number) => {
            const option = new EveryCigVoteScreenOption(game, part, group, currentX, currentY, this.unselectOptions, debug)
            if (index === 1) {
                currentY += option.option.height + option.title.height + Scale.y(0.002)
                currentX = x
            } else {
                currentX += option.option.width + Scale.x(0.01)
            }
            return option
        })

        if (!EveryCigStore.instance.voteAudio) {
            Audio.add(game, EveryCigPart1Audio.CHOOSE_GROSSEST, {
                onPlay: () => {
                    this.doneButton.disable()
                    this.backButton.disable()
                    this.options.forEach(option => option.disable())
                },
                onStop: () => {
                    this.backButton.enable()
                    this.options.forEach(option => option.enable())
                    EveryCigStore.instance.voteAudio = true
                }
            })
        }

        if (EveryCigStore.instance.selected) {
            const [selected] = this.options.filter(option => option.part === EveryCigStore.instance.selected)
            selected.selected = true
            this.doneButton.enable()
        }
    }

    private submitVote = () => {
        StudentResultsStore.instance.makeEveryCigChoice(EveryCigStore.instance.selected)
        this.screen.addClose(this.onVote)
        this.screen.zoomOut(() => this.group.destroy(true))
    }

    private unselectOptions = (part: string) => {
        this.options.filter(option => option.part !== part).forEach(option => option.selected = false)
        this.doneButton.enable()
    }

    private onBackClick = () => this.screen.zoomOut(() => this.group.destroy(true))
}

class EveryCigVoteScreenOption {
    option: Phaser.Sprite
    title: Phaser.Text
    private frame: Rectangle

    constructor(
        private game: Phaser.Game,
        public part: EveryCigBodyPart,
        group: Phaser.Group,
        x: number,
        y: number,
        private unselectButtons: (part: string) => void,
        debug: boolean
    ) {
        this.option = Sprites.add(game, everyCigMoviePreviewImage(part), {group, debug, x, y, scale: 0.1})
        this.frame = new Rectangle(game, this.option.x, this.option.y, this.option.width, this.option.height, {
            group,
            lineColor: Phaser.Color.RED,
            lineWidth: 4
        })
        this.frame.graphics.alpha = 0
        this.title = PhaserText.create(game, part, {group, weight: "bold", align: Align.CENTER, fontPx: 12})
        Sprites.centerTextInSprite(this.option, this.title)
        this.title.y += (this.option.height / 2) + Scale.y(0.02)
        this.enable()
    }

    enable = () => Buttons.makeSpriteButton(this.option, () => {
        this.selected = true
        EveryCigStore.instance.selected = this.part
        this.unselectButtons(this.part)
    })

    disable = () => Buttons.undoSpriteButton(this.option)

    get selected(): boolean {
        return !!this.frame.graphics.alpha
    }

    set selected(s: boolean) {
        this.frame.graphics.alpha = s ? 1 : 0
    }
}
